/**
 * Single source of truth for every piece of site content.
 * Pages render this — they do not hardcode copy. Updating the resume means
 * editing this file, not JSX.
 */

const asset = (file) => `${process.env.PUBLIC_URL}/assets/${file}`;

export const profile = {
  name: "Akhil Jayendran",
  title: "Observability Architect",
  location: "Plano, TX",
  yearsExperience: 20,
  tagline: "I make complex systems legible.",
  intro:
    "Twenty years designing, configuring, and implementing monitoring and observability solutions for enterprise customers — from APM rollouts to OpenTelemetry migrations.",
  bio: [
    "With over 20 years in the monitoring and observability domain, I have worked with organisations across industries to design, configure, and implement tailored observability solutions.",
    "I have a strong interest in Site Reliability Engineering and AIOps, where I focus on automation, continuous improvement, and AI-driven insights to address issues before they reach the end user.",
    "I am committed to fostering a culture of reliability and performance excellence across the organisations I work with.",
  ],
  education:
    "Bachelor's in Electronics and Communication, Anna University — plus professional certifications across monitoring and cloud platforms.",
  resumePdf: asset("Akhil_Jayendran.pdf"),
  resumeImage: asset("Resume.png"),
  photo: asset("profilep.jpeg"),
};

export const links = {
  github: "https://github.com/Akhilja",
  linkedin: "https://www.linkedin.com/in/akhilja/",
  email: "mailto:ja.akhil@gmail.com",
  certDirectory:
    "https://certdirectory.io/profile/8e73f3bb-e7b1-4162-a22c-c20b654c6f08",
};

/** `icon` is a react-icons key resolved in Layout — vector marks inherit the
 *  theme text colour, unlike the dark PNG badges they replaced. */
export const socials = [
  { label: "GitHub", href: links.github, icon: "github" },
  { label: "LinkedIn", href: links.linkedin, icon: "linkedin" },
  { label: "Email", href: links.email, icon: "mail" },
];

export const stats = [
  { value: "20+", label: "Years in observability" },
  { value: "20", label: "Active certifications" },
  { value: "6", label: "Global enterprises" },
];

/** Skill tiles, grouped. `icon` filenames must match public/assets casing exactly. */
export const skillGroups = [
  {
    name: "Observability",
    items: [
      { label: "Dynatrace", icon: asset("DT.png") },
      { label: "New Relic", icon: asset("NR.png") },
      { label: "Prometheus", icon: asset("prom.png") },
      { label: "Grafana", icon: asset("grafana.png") },
      { label: "OpenTelemetry", icon: asset("OpenTelemetry.svg") },
      { label: "Datadog", icon: asset("datadog.svg"), accent: "datadog" },
      { label: "ServiceNow Event Management", icon: asset("servicenow.svg") },
    ],
  },
  {
    name: "Automation & Delivery",
    items: [
      { label: "Jenkins", icon: asset("Jenkins.png") },
      { label: "Ansible", icon: asset("ansible.svg") },
      { label: "Terraform", icon: asset("terraform.png") },
      { label: "GitHub", icon: asset("GitHubCard.png") },
      { label: "Python", icon: asset("Python.png") },
    ],
  },
  {
    name: "Cloud & Platform",
    items: [
      { label: "AWS", icon: asset("aws.png") },
      { label: "Azure", icon: asset("azure.png") },
      { label: "Kubernetes", icon: asset("kubernetes.png") },
    ],
  },
  {
    name: "AI & Agentic Engineering",
    items: [
      { label: "Model Context Protocol (MCP)", icon: asset("mcp.svg"), accent: "mcp" },
      { label: "Claude", icon: asset("claude.svg"), accent: "claude" },
      { label: "Agent Skills", icon: asset("agent-skills.svg"), accent: "agent" },
    ],
  },
];

export const expertise = [
  {
    area: "AI & Agentic Engineering",
    tools: ["Model Context Protocol (MCP)", "Claude", "Agent Skills"],
  },
  {
    area: "Application Performance Monitoring",
    tools: ["Dynatrace", "New Relic", "Datadog", "Grafana", "Prometheus", "OpenTelemetry"],
  },
  {
    area: "Infrastructure Monitoring",
    tools: ["IBM Tivoli", "SCOM", "Nagios", "ServiceNow Event Management"],
  },
  {
    area: "Network Monitoring",
    tools: ["Zabbix", "SNMP"],
  },
  {
    area: "Working knowledge",
    tools: ["AppDynamics", "ELK Stack", "Splunk"],
  },
];

export const experience = [
  { company: "TEKsystems Global Services", start: "Sep 2025", end: "Present" },
  { company: "SK Technologies", start: "Feb 2025", end: "Sep 2025" },
  { company: "Allianz Technology SE", start: "Sep 2011", end: "Jan 2025" },
  { company: "IBM India", start: "Sep 2009", end: "Aug 2011" },
  { company: "Wipro Technologies", start: "Jan 2009", end: "Sep 2009" },
  { company: "Tata Consultancy Services", start: "Apr 2007", end: "Dec 2008" },
];

export const softSkills = [
  { title: "Problem Solving", body: "Breaking down complex problems and finding efficient solutions." },
  { title: "Communication", body: "Explaining technical concepts clearly to non-technical stakeholders." },
  { title: "Time Management", body: "Managing multiple workstreams and meeting deadlines." },
  { title: "Critical Thinking", body: "Analysing situations and making evidence-based decisions." },
  { title: "Collaboration", body: "Working across teams and contributing to shared outcomes." },
  { title: "Adaptability", body: "Learning new technologies and adjusting to changing requirements." },
  { title: "Leadership", body: "Taking initiative, mentoring others, and driving projects to completion." },
  { title: "Mentorship", body: "Sharing knowledge and fostering growth in junior engineers." },
];

export const certifications = [
  ...Array.from({ length: 14 }, (_, i) => ({
    src: asset(`certification${i + 1}.png`),
    alt: `Certification ${i + 1}`,
  })),
  {
    src: "https://images.credly.com/images/e50c657a-edd9-4c93-b1cf-2b6634b54abf/blob",
    alt: "AWS Educate Introduction to Generative AI",
  },
  {
    src: "https://images.credly.com/images/dfdf5570-75c8-4734-a71c-6ed00ce27382/blob",
    alt: "Dynatrace Beginner",
  },
  {
    src: "https://images.credly.com/images/86505a3f-b8ec-4214-8f00-98c16c8b0bd0/blob",
    alt: "Dynatrace Customer Success",
  },
  {
    src: "https://images.credly.com/images/7fcca551-9cd2-458e-a5cd-7406093b8bd2/blob",
    alt: "Dynatrace Partner Sales Specialist",
  },
  {
    src: "https://images.credly.com/images/3d3f7131-83a4-4427-8a68-150ca90bcc23/blob",
    alt: "OpenTelemetry Certified Associate",
  },
  {
    src: "https://images.credly.com/images/6260bd98-5ea5-4e42-96bf-0bcea7f1a7df/blob",
    alt: "Dynatrace Essentials",
  },
];
